import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import type { ApprovalEmailSetting, ApprovalItem, ApprovalList, ApprovalStatus, useProductLines } from '../hooks/useProductLines';

/**
 * Approval queue for product lines set to "Wait for approval": new supplier products that
 * match such a line wait here instead of going to the store. Approved products are imported
 * at the next sync; rejected ones stay out on every later sync (any product line), until
 * approved from the Rejected tab.
 */

type Api = Pick<ReturnType<typeof useProductLines>, 'listApprovals' | 'decide' | 'approvalEmail'>;

interface Props {
  api: Api;
  // Changes whenever the lines reload (after a save or a decision), so the queue refetches.
  version: unknown;
}

const PAGE = 100;

const ProductApprovalQueue: React.FC<Props> = ({ api, version }) => {
  const toast = useToast();
  // useToast() returns a new object on every toast change; keep it out of load's deps so
  // a failing load cannot re-trigger itself through its own error toast.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const { listApprovals, decide, approvalEmail } = api;
  const [tab, setTab] = useState<ApprovalStatus>('pending');
  const [data, setData] = useState<ApprovalList | null>(null);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState<ApprovalEmailSetting | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await listApprovals(tab, offset, PAGE));
    } catch (e: any) {
      toastRef.current.error(e.message);
    }
  }, [listApprovals, tab, offset]);

  useEffect(() => {
    load();
  }, [load, version]);

  useEffect(() => {
    setSelected(new Set());
  }, [tab, offset, data]);

  useEffect(() => {
    approvalEmail().then(setEmail).catch(() => setEmail(null));
  }, [approvalEmail]);

  const run = async (skus: string[], decision: 'approved' | 'rejected') => {
    if (!skus.length) return;
    setBusy(true);
    try {
      const { changed, refresh_error } = await decide(skus, decision);
      if (refresh_error) toast.error(`Saved, but re-matching failed: ${refresh_error}. It retries within the hour.`);
      else toast.success(decision === 'approved'
        ? `Approved ${changed} product${changed === 1 ? '' : 's'} — imported at the next sync.`
        : `Rejected ${changed} product${changed === 1 ? '' : 's'} — they stay out of your store.`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleEmail = async () => {
    try {
      if (email) setEmail(await approvalEmail(!email.enabled));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const items = data?.items ?? [];
  const total = data?.counts[tab] ?? 0;
  const allOnPage = items.length > 0 && items.every((i) => selected.has(i.sku));
  const toggle = (sku: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(sku)) n.delete(sku); else n.add(sku);
      return n;
    });

  const row = (i: ApprovalItem) => (
    <li key={i.sku} className="py-2 flex items-center gap-3">
      <input type="checkbox" checked={selected.has(i.sku)} onChange={() => toggle(i.sku)} aria-label={`Select ${i.sku}`} />
      {i.photo_url ? (
        <img
          src={i.photo_url}
          alt=""
          className="w-12 h-12 object-cover rounded bg-white/5 shrink-0"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
      ) : (
        <div className="w-12 h-12 rounded bg-white/5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm truncate" title={i.title ?? i.sku}>{i.title || i.sku}</div>
        <div className="text-white/50 text-xs truncate">
          {i.sku}
          {' · '}{i.stock > 0 ? `${i.stock} in stock` : 'no stock yet'}
          {i.price != null && ` · from €${Number(i.price).toFixed(2)}`}
          {i.suppliers.length > 0 && ` · ${i.suppliers.map((s) => s.name).join(', ')}`}
          {i.lines.length > 0 && ` · ${i.lines.map((l) => l.name).join(', ')}`}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {tab !== 'approved' && (
          <button disabled={busy} onClick={() => run([i.sku], 'approved')} className="px-2 py-1 text-xs rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
            Approve
          </button>
        )}
        {tab !== 'rejected' && (
          <button disabled={busy} onClick={() => run([i.sku], 'rejected')} className="px-2 py-1 text-xs rounded border border-white/15 text-white/60 hover:text-red-300 disabled:opacity-50">
            Reject
          </button>
        )}
      </div>
    </li>
  );

  return (
    <section className="glass-card rounded-xl p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-white font-semibold text-lg">Waiting for approval</h2>
          <p className="text-white/60 text-sm mt-1">
            New supplier products matching a line set to "Wait for approval". They are not in
            your store until you approve them. Rejected products stay out on later syncs.
          </p>
        </div>
        {email !== null && (
          <div className="text-sm text-white/70 shrink-0">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={email.enabled}
                disabled={!email.allowed && !email.enabled}
                onChange={toggleEmail}
              />
              Email me when new products are waiting
            </label>
            <p className="text-white/40 text-xs mt-1 ml-5">
              {email.allowed
                ? `Sent to ${email.email ?? 'your login email'}, within the hour. Each team member chooses for themselves.`
                : 'Only owners and admins of this store can get this email.'}
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {(['pending', 'rejected', 'approved'] as ApprovalStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => { setTab(s); setOffset(0); }}
            className={`px-3 py-1 text-xs rounded border ${tab === s ? 'border-orange-500 text-white' : 'border-white/15 text-white/60'}`}
          >
            {s === 'pending' ? 'Waiting' : s === 'rejected' ? 'Rejected' : 'Approved'} ({data?.counts[s] ?? 0})
          </button>
        ))}
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-3 text-sm flex-wrap">
          <label className="flex items-center gap-2 text-white/70">
            <input
              type="checkbox"
              checked={allOnPage}
              onChange={() => setSelected(allOnPage ? new Set() : new Set(items.map((i) => i.sku)))}
            />
            Select all on this page
          </label>
          {selected.size > 0 && (
            <>
              {tab !== 'approved' && (
                <button disabled={busy} onClick={() => run(Array.from(selected), 'approved')} className="px-3 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  Approve {selected.size}
                </button>
              )}
              {tab !== 'rejected' && (
                <button disabled={busy} onClick={() => run(Array.from(selected), 'rejected')} className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                  Reject {selected.size}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {!data ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-white/50 text-sm">
          {tab === 'pending' ? 'Nothing is waiting.' : tab === 'rejected' ? 'No rejected products.' : 'No approved products.'}
        </p>
      ) : (
        <ul className="divide-y divide-white/10">{items.map(row)}</ul>
      )}

      {total > PAGE && (
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <div className="flex gap-2">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="px-2 py-1 border border-white/15 rounded disabled:opacity-40">Previous</button>
            <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} className="px-2 py-1 border border-white/15 rounded disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductApprovalQueue;
